/**
 * One app-wide connectivity and sync reading.
 * Online is not the same as synced. Counts come only from queues CrewPilot
 * already has: picture metadata, queued picture notes, and pending time entries.
 * Materials ride on the time-entry queue and are not counted again.
 */

import { isOffline } from "./connectivity";
import { getScopedPendingTimelogs } from "./offlineCacheScope";
import {
  isPictureSyncDraining,
  listLocalPictureNotes,
  listLocalPictures,
} from "./pictureSyncStore";

export const SYNC_HEALTH = Object.freeze({
  SYNCED: "synced",
  SYNCING: "syncing",
  OFFLINE: "offline",
  ATTENTION: "attention",
});

const ATTENTION = "needs_attention";
const LAST_SYNC_PREFIX = "cp_last_successful_sync:";

export function deriveSyncHealth({ offline, syncing, pending, attention }) {
  if (attention > 0) return SYNC_HEALTH.ATTENTION;
  if (offline) return SYNC_HEALTH.OFFLINE;
  if (syncing || pending > 0) return SYNC_HEALTH.SYNCING;
  return SYNC_HEALTH.SYNCED;
}

function plural(count, singular, pluralLabel = `${singular}s`) {
  return `${count} ${count === 1 ? singular : pluralLabel}`;
}

export function syncStatusLabel(snapshot) {
  if (!snapshot) return "Synced";
  const pendingCount = snapshot.pendingCount || 0;
  const attentionCount = snapshot.attentionCount || 0;
  if (attentionCount > 0) {
    const noun = attentionCount === 1 ? "1 needs attention" : `${attentionCount} need attention`;
    return snapshot.offline ? `Offline · ${noun}` : noun;
  }
  if (snapshot.offline) {
    return pendingCount ? `Offline · ${pendingCount} pending` : "Offline";
  }
  if (snapshot.health === SYNC_HEALTH.SYNCING || pendingCount > 0) {
    return pendingCount ? `Syncing · ${pendingCount}` : "Syncing";
  }
  return "Synced";
}

export function syncStatusGlyph(snapshot) {
  const label = syncStatusLabel(snapshot);
  if (label.includes("need")) return "⚠";
  if (snapshot?.offline) return "◌";
  if (label.startsWith("Syncing")) return "↻";
  return "●";
}

export function syncStatusLines(snapshot) {
  const lines = [];
  if (snapshot?.pictures) lines.push(plural(snapshot.pictures, "picture"));
  if (snapshot?.timeEntries) lines.push(plural(snapshot.timeEntries, "time entry", "time entries"));
  if (snapshot?.notes) lines.push(plural(snapshot.notes, "note"));
  return lines;
}

function lastSyncKey(userId) {
  return `${LAST_SYNC_PREFIX}${userId || "device"}`;
}

export function recordSuccessfulSync(userId) {
  try {
    localStorage.setItem(lastSyncKey(userId), new Date().toISOString());
  } catch {
    // The sync itself succeeded. The timestamp is only a display hint.
  }
}

export function readLastSuccessfulSync(userId) {
  try {
    return localStorage.getItem(lastSyncKey(userId)) || "";
  } catch {
    return "";
  }
}

export function formatSyncTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}

export async function readSyncStatus(actor) {
  const organizationId = actor?.organization_id || null;
  const createdBy = actor?.supabase_user_id || null;
  const offline = isOffline();
  let pictures = [];
  let notes = [];
  try {
    pictures = await listLocalPictures({ organizationId, createdBy });
    notes = await listLocalPictureNotes({ organizationId, createdBy });
  } catch {
    pictures = [];
    notes = [];
  }
  const pendingPictures = pictures.filter((row) => row.sync_state !== ATTENTION);
  const attentionPictures = pictures.filter((row) => row.sync_state === ATTENTION);
  const pendingNotes = notes.filter((row) => row.sync_state !== ATTENTION);
  const attentionNotes = notes.filter((row) => row.sync_state === ATTENTION);
  let timeEntries = 0;
  try {
    timeEntries = getScopedPendingTimelogs(actor).length;
  } catch {
    timeEntries = 0;
  }
  const pending = pendingPictures.length + pendingNotes.length + timeEntries;
  const attention = attentionPictures.length + attentionNotes.length;
  const syncing = isPictureSyncDraining();
  const health = deriveSyncHealth({ offline, syncing, pending, attention });
  return {
    offline,
    online: !offline,
    syncing,
    health,
    pendingCount: pending,
    attentionCount: attention,
    pictures: pendingPictures.length,
    notes: pendingNotes.length,
    timeEntries,
    attentionPictures: attentionPictures.length,
    attentionNotes: attentionNotes.length,
    attentionMessage: attentionPictures[0]?.attention_message || attentionNotes[0]?.attention_message || "",
    lastSyncAt: readLastSuccessfulSync(createdBy),
  };
}

export function requestSyncNow() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent("crewpilot-sync-now"));
}
